import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import EmailIcon from '@mui/icons-material/Email'
import { marked } from 'marked'
import { API_PATH_REPORT_AUDIT_SEND } from '../../../config'
import { getApiConfig, getLocalServerURL } from '../../../utils/getApiConfig'
import { onError } from '../../../utils/onError'
import { onSuccess } from '../../../utils/onSuccess'

marked.setOptions({
  gfm: true,
  breaks: true,
})

const MODAL_TITLE = 'Storage & billing audit report'

export interface AuditReportData {
  markdown: string
  generatedAt: string
  format: string
}

interface AuditReportModalProps {
  open: boolean
  onClose: () => void
  report: AuditReportData | null
  onError?: (message: string) => void
}

export const AuditReportModalComponent: React.FC<AuditReportModalProps> = ({
  open,
  onClose,
  report,
  onError: onErrorProp,
}) => {
  const [emailSending, setEmailSending] = React.useState(false)
  const showError = onErrorProp ?? onError

  const handleEmailReport = React.useCallback(async () => {
    setEmailSending(true)
    try {
      const config = await getApiConfig({ body: {} })
      if (config == null) {
        showError('Could not authorize')
        return
      }
      const response = await fetch(
        getLocalServerURL(API_PATH_REPORT_AUDIT_SEND),
        { ...config, method: 'POST', body: JSON.stringify({}) }
      )
      if (!response.ok) {
        showError('Failed to send report by email')
        return
      }
      onSuccess('Report sent by email')
    } catch {
      showError('Failed to send report by email')
    } finally {
      setEmailSending(false)
    }
  }, [showError])

  const formattedDate = report?.generatedAt
    ? new Date(report.generatedAt).toLocaleString()
    : ''

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" variant="h6">
          {MODAL_TITLE}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ ml: 'auto' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          overflowY: 'auto',
          minHeight: 200,
          maxHeight: '70vh',
          '& .markdown-body': {
            fontSize: '0.95rem',
            lineHeight: 1.6,
          },
          '& .markdown-body h1, & .markdown-body h2, & .markdown-body h3': {
            marginTop: '16px',
            marginBottom: '8px',
          },
          '& .markdown-body table': {
            borderCollapse: 'collapse',
            width: '100%',
          },
          '& .markdown-body th, & .markdown-body td': {
            border: '1px solid',
            borderColor: 'divider',
            padding: '8px',
          },
          '& .markdown-body p': {
            marginBottom: '8px',
          },
        }}
      >
        {report?.generatedAt && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generated: {formattedDate}
          </Typography>
        )}
        {report?.markdown ? (
          <div
            className="markdown-body"
            dangerouslySetInnerHTML={{
              __html: marked.parse(report.markdown) as string,
            }}
          />
        ) : (
          <Typography color="text.secondary">No report content.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<EmailIcon />}
          onClick={handleEmailReport}
          disabled={emailSending || !report}
        >
          {emailSending ? 'Sending…' : 'Email report'}
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
